import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Container, useTheme } from "@mui/material";
import { postNotification } from "../../components/utilities/Notifications";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import AccessDenied from "../utility/AccessDenied";
import {
  getData,
  titleCase
} from "../../components/utilities/UtilityFunctions";
import sanitizeHtml from "sanitize-html";
import CaptchaVerification from "../../components/utilities/CaptchaVerification";
import { setCookie } from "../../components/layout/cookies/CookieFunctions";
import { isAppMode } from "../../global/AppOverlay";

export default function ArmyList(props) {
  const { setEvent } = props;
  let { armyListId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const appMode = isAppMode();

  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(null);
  const [armyList, setArmyList] = useState(null);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const authContext = useContext(AuthContext);

  const fetchArmyList = async () => {
    try {
      if (armyListId) {
        if (authContext?.authState?.cookieConsent) {
          setCookie("lastListId", armyListId);
        }
      }

      var apiGetData = await getData(`/armylists/${armyListId}`, {
        queryStringParameters: {}
      });
      if (apiGetData?.event) {
        if (authContext?.authState?.cookieConsent) {
          setCookie("lastEventId", apiGetData?.event?.id);
        }
        setEvent(apiGetData.event);
      }
      setArmyList(apiGetData);
      setCaptchaRequired(false);
    } catch (err) {
      postNotification("error", err);

      const errStatus = parseInt(err?.message?.split(":")[0]);
      const errMessage = err?.message?.split(":").slice(1).join(":").trim();

      if (errStatus === 404) {
        if (appMode) {
          setAccessDenied(titleCase(errMessage || "Access Denied"));

          return;
        } else navigate("/home");
        return;
      } else if (errStatus === 403) {
        setAccessDenied(titleCase(errMessage || "Access Denied"));
        if (!appMode && errMessage === "access denied: subscriber feature") {
          navigate("/subscription?subFeature=true");
          return;
        }
      } else if (errStatus === 423) {
        setCaptchaRequired(true);
        return;
      } else {
        // Catch all other errors in app mode
        if (appMode) {
          setAccessDenied(titleCase(errMessage || "Access Denied"));
          return;
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCaptchaVerificationSuccess = async () => {
    setLoading(true);
    await fetchArmyList();
  };

  useEffect(() => {
    fetchArmyList();
    // eslint-disable-next-line
  }, []);

  const addAnchorTags = (listString) => {
    const regex = /https:\/\/onepiece-cardgame\.dev[^<]*?<br>/g;
    const result = listString.replace(regex, (match) => {
      const url = match.split("<br>")[0];
      return `<a href="${url}" target="_blank" style="text-decoration:underline;color:${theme?.palette?.link.main}">${url}</a><br>`;
    });

    return result;
  };

  return (
    <>
      {loading && <ModalLoadingFull />}
      {!loading && captchaRequired && (
        <CaptchaVerification
          onVerificationSuccess={handleCaptchaVerificationSuccess}
          onGoBack={() => navigate(-1)}
        />
      )}
      {!loading && !captchaRequired && !accessDenied && (
        <Box
          sx={{
            background:
              theme?.palette?.mode === "dark"
                ? "rgba(0,0,0,0.7)"
                : "rgba(255,255,255,0.7)",
            minHeight: "calc(100vh - 64px)",
            mb: -2,
            "& .list":
              theme?.palette?.mode === "dark"
                ? {
                    backgroundColor: "rgba(0,0,0,0.7)"
                  }
                : null
          }}
        >
          <Container
            sx={{
              pt: 2,
              pb: 2
            }}
            maxWidth="lg"
          >
            {armyList?.armyListHTML && (
              <Box
                dangerouslySetInnerHTML={{
                  __html: sanitizeArmyList(addAnchorTags(armyList.armyListHTML))
                }}
              />
            )}
          </Container>
        </Box>
      )}
      {!loading && accessDenied && <AccessDenied message={accessDenied} />}
    </>
  );
}

const sanitizeArmyList = (listHtml) => {
  if (!listHtml) return "<p>Error</p>";

  return sanitizeHtml(listHtml, {
    allowedTags: [
      "h2",
      "h3",
      "p",
      "a",
      "body",
      "span",
      "div",
      "br",
      "link",
      "style",
      "img"
    ],
    allowedAttributes: {
      p: ["style", "align"],
      h2: ["align"],
      h3: ["align"],
      a: ["href", "name", "target", "style"],
      img: ["src", "alt", "width", "height"],
      link: ["href", "rel"],
      body: [
        "background-image",
        "background-attachment",
        "background-repeat",
        "background-position",
        "background-size"
      ]
    },
    allowedStyles: {
      p: ["font-size", "font-style"],
      a: ["text-decoration", "color"]
    },
    allowedClasses: {
      div: ["body", "list", "found", "errors", "notFound", "info"],
      span: [
        "found",
        "foundResponse",
        "error",
        "notFound",
        "notFoundReason",
        "info"
      ]
    }
  });
};
